import type { Route } from '../App';
import { Icon, type IconName } from '../components/Icon';
import { Avatar, HealthDot } from '../components/bits';
import { useClients, useOverview, useWorkspace } from '../data/store';
import { euro, kpis } from '../data/helpers';
import type { Activity, Client } from '../data/types';

interface Props {
  onOpenClient: (id: string) => void;
  onGo: (r: Route) => void;
  onNewClient: () => void;
}

const KIND_ICON: Record<Activity['kind'], { icon: IconName; warn?: boolean }> = {
  publish: { icon: 'spark' },
  booking: { icon: 'calendar' },
  payment: { icon: 'check' },
  signup: { icon: 'users' },
  domain: { icon: 'globe' },
  message: { icon: 'message' },
  alert: { icon: 'alert', warn: true },
};

export function OverviewView({ onOpenClient, onGo, onNewClient }: Props) {
  const clients = useClients();
  const { activity, revenue } = useOverview();
  const { current } = useWorkspace();
  const houseWide = current === 'all';
  const k = kpis(clients);
  const mrrDelta = revenue.length >= 4 ? revenue[revenue.length - 1]!.mrr - revenue[revenue.length - 4]!.mrr : 0;
  const max = revenue.length ? Math.max(...revenue.map((r) => r.mrr)) : 0;
  // Most urgent first: overdue → unpaid setup → other attention.
  const urgency = (c: Client) => (c.health === 'overdue' ? 0 : !c.setupPaid ? 1 : 2);
  const attention = clients
    .filter((c) => c.health !== 'good' || !c.setupPaid)
    .sort((a, b) => urgency(a) - urgency(b));
  const visibleIds = new Set(clients.map((c) => c.id));
  const feed = current === 'all' ? activity : activity.filter((a) => a.clientId && visibleIds.has(a.clientId));

  return (
    <div className="view">
      <div className="view-pad rs-stack">
        {/* KPIs */}
        <div className="kpi-row">
          <Kpi icon="building" val={String(k.active)} label="Clienti attivi" />
          <Kpi icon="chart" val={euro(k.mrr)} label="Ricavo mensile (MRR)" delta={houseWide ? `+${euro(mrrDelta)}/3 mesi` : undefined} />
          <Kpi icon="globe" val={String(k.liveSites)} label="Siti online" />
          <Kpi icon="spark" val={String(k.onboarding)} label="In lavorazione" />
        </div>

        <div className="rs-2col">
          {/* left: activity */}
          <div className="rs-stack">
            <div className="rs-card">
              <div className="rs-card-head">
                <span className="rs-card-title">Attività recente</span>
              </div>
              <div className="rs-feed">
                {feed.map((a) => {
                  const m = KIND_ICON[a.kind];
                  return (
                    <button
                      key={a.id}
                      type="button"
                      className="rs-feed-item"
                      style={{ background: 'none', border: 0, borderBottom: '1px solid var(--rule-2, var(--rule))', textAlign: 'left', cursor: a.clientId ? 'pointer' : 'default', width: '100%' }}
                      onClick={() => a.clientId && onOpenClient(a.clientId)}
                    >
                      <span className={'rs-feed-ico' + (m.warn ? ' warn' : '')}>
                        <Icon name={m.icon} size={15} />
                      </span>
                      <span className="rs-feed-body">
                        <span className="rs-feed-text">
                          <b>{a.clientName}</b> {a.text}
                        </span>
                        <span className="rs-feed-time">{a.time}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* right: quick actions + attention + revenue */}
          <div className="rs-stack">
            <div className="rs-card">
              <div className="rs-card-head">
                <span className="rs-card-title">Azioni rapide</span>
              </div>
              <div className="rs-quick">
                <button type="button" onClick={() => onGo('clients')}>
                  <Icon name="building" size={17} className="ico" /> Tutti i clienti
                </button>
                <button type="button" onClick={() => onGo('pipeline')}>
                  <Icon name="list" size={17} className="ico" /> Pipeline
                </button>
                <button type="button" onClick={() => onGo('revenue')}>
                  <Icon name="chart" size={17} className="ico" /> Ricavi
                </button>
                <button type="button" onClick={onNewClient}>
                  <Icon name="plus" size={17} className="ico" /> Nuovo cliente
                </button>
              </div>
            </div>

            <div className="rs-card">
              <div className="rs-card-head">
                <span className="rs-card-title">Da seguire</span>
                <span className="rs-muted" style={{ fontSize: 12 }}>{attention.length}</span>
              </div>
              <div className="rs-stack" style={{ gap: 10 }}>
                {attention.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    className="rs-mini-top"
                    style={{ background: 'none', border: 0, padding: '2px 0', cursor: 'pointer', width: '100%' }}
                    onClick={() => onOpenClient(c.id)}
                  >
                    <Avatar client={c} size={30} />
                    <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, textAlign: 'left' }}>
                      <span className="rs-mini-name">{c.name}</span>
                      <span className="rs-mini-meta">{!c.setupPaid ? 'Saldo setup da incassare' : c.health === 'overdue' ? 'Fatture insolute' : 'Fattura in scadenza'}</span>
                    </span>
                    <span style={{ marginLeft: 'auto' }}>
                      <HealthDot health={c.health === 'overdue' ? 'overdue' : 'attention'} withLabel />
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="rs-card">
              <div className="rs-card-head">
                <span className="rs-card-title">Andamento ricavi</span>
                <button type="button" className="rs-card-link" onClick={() => onGo('revenue')}>
                  Dettaglio
                </button>
              </div>
              <div className="rs-bigstat">
                <span className="v">{euro(k.mrr)}</span>
                <span className="u">/ mese</span>
              </div>
              {houseWide ? (
                <div className="rs-chart" style={{ height: 90 }}>
                  {revenue.map((r) => (
                    <div className="bar-col" key={r.label}>
                      <div className="bar" style={{ height: '100%' }}>
                        <div className="fill" style={{ height: `${(r.mrr / max) * 100}%` }} />
                      </div>
                      <span style={{ fontSize: 10, color: 'var(--mute)' }}>{r.label}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <span className="rs-muted" style={{ fontSize: 12 }}>
                  L’andamento storico è disponibile su “Tutti gli spazi”.
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Kpi({ icon, val, label, delta }: { icon: IconName; val: string; label: string; delta?: string }) {
  return (
    <div className="kpi">
      <div className="kpi-top">
        <span className="kpi-ico">
          <Icon name={icon} size={17} />
        </span>
        {delta && <span className="kpi-delta">{delta}</span>}
      </div>
      <span className="kpi-val">{val}</span>
      <span className="kpi-label">{label}</span>
    </div>
  );
}
